<template>
  <div class="chart-wrapper">
    <div class="toolbar">
      <button @click="updateData(100)"  :class="{active: selection==='100'}">100</button>
      <button @click="updateData(500)"  :class="{active: selection==='500'}">500</button>
      <button @click="updateData(1000)" :class="{active: selection==='1000'}">1000</button>
    </div>
    <apexchart ref="apexChart" height="90%" :options="autoRegularOptions" :series="autoRegularData"></apexchart>
    <p class="align-center mt-10">LLP: {{ kpis.llpRebuys }} | P/L (LLP + Regs): {{  kpisDataCalcs.DeltaPLRegularsRebuys }} | Open P/L: {{ kpisDataCalcs.plRegulars.toFixed(2) }} | Allocated Capital: $ {{ kpisDataCalcs.allocatedCapitalRegulars.toFixed(2) || 0 }}</p>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { defaultChartOptions } from '@/utils/globalApexConfig';
import { calcPL } from '@/utils/funcs.js';
import Big from 'big.js';

export default {
  name: 'portfolioHealth',
  props: {
    intervalChart: { type: Number, required: true },
  },
  data() {
    return {
      interval: this.intervalChart,
      selection: `${this.intervalChart}`,
    };
  },
  computed: {
    ...mapState({ marketData: state => state.marketData, kpis: state => state.kpis }),
    ...mapGetters(['regularHealthCostByBin', 'kpisDataCalcs']),

    // ─── Tier 1: cached until ledger or interval changes ─────────────────────
    costStructure() {
      return this.regularHealthCostByBin(this.interval);
    },

    // ─── Tier 2: only drawdown re-runs on price ticks ─────────────────────────
    healthData() {
      const marketPrice = Number(this.marketData.btcUsd ?? 0);
      return this.costStructure.map(bin => {
        const drawdown = bin.binUnits.reduce((acc, u) => {
          const qty      = Number(u.current?.qty ?? 0);
          const avgPrice = Number(u.current?.avgPrice ?? 0);
          return acc.plus(new Big(calcPL(marketPrice, avgPrice, qty)));
        }, new Big(0));
        return {
          x:         bin.x,
          allocated: bin.allocated,
          drawdown:  parseFloat(drawdown.toFixed(2)),
          llp:       bin.llp,
        };
      });
    },

    autoRegularData() {
      if (!this.healthData.length) return [];
      return [
        { name: 'Allocated',  data: this.healthData.map(d => d.allocated) },
        { name: 'Drawdown',   data: this.healthData.map(d => d.drawdown) },
        { name: 'LLP Rebuys', data: this.healthData.map(d => d.llp) },
      ];
    },

    // ─── Bin label closest to currentPrice (for annotation) ──────────────────
    closestBinLabel() {
      if (!this.healthData.length) return null;
      const price = Number(this.marketData.btcUsd ?? 0);
      return this.healthData.reduce((closest, bin) => {
        return Math.abs(parseFloat(bin.x) - price) < Math.abs(parseFloat(closest.x) - price)
          ? bin : closest;
      }).x;
    },

    autoRegularOptions() {
      return {
        ...defaultChartOptions,
        chart: { type: 'bar', stacked: false, background: 'var(--table-header-bgcolor)'},
        stroke: { show: false},
        plotOptions: {
          bar: { barHeight: '90%', horizontal: true, borderRadius: 4 },
        },
        annotations: {
          yaxis: this.closestBinLabel ? [{
            y: this.closestBinLabel,
            borderColor: '#00E396',
            label: {
              borderColor: '#00E396',
              style: { color: '#fff', background: '#00E396' },
              text: 'Faixa atual',
            }
          }] : [],
        },
        xaxis: {
          type: 'category',
          categories: this.healthData.map(d => d.x),
        },
      };
    }
  },
  methods: {
    updateData(interval) {
      this.selection = `${interval}`;
      this.interval = interval;
    }
  }
}
</script>
<style scoped>
.toolbar button {
  background-color: aliceblue;
  width: 30px;
  height: 20px;
  color: #333;
  margin-right: 4px;
  font-size: 8px !important;
  padding: 0 4px !important;
}
.active {
    background-color: #333 !important;
    color: #fff !important;
}
</style>
